import { createAsyncThunk, createSlice, type PayloadAction } from "@reduxjs/toolkit";
import { DocumentApi } from "~/functions/api/document";
import { ProcessLockApi } from "~/functions/api/processLock";
import type { RootState } from "~/store";

interface RowData {
  機能名: string;
  適用アプリ名: string;
  概要: string;
  入力フィールド: string;
  出力フィールド: string;
  処理条件: string;
  機能詳細: string;
  設定情報: string;
  イベント契機: string;
  対応端末: string;
  対応区分: string;
}

interface CustomizeSpecificationState {
  data: RowData | null;
  copyData: RowData | null;
  isEditable: boolean;
  isLoading: boolean;
  error: string | null;
  fetchError: string | null;
}

const initialState: CustomizeSpecificationState = {
  data: null,
  copyData: null,
  isEditable: false,
  isLoading: true,
  error: null,
  fetchError: null,
};

// APIインスタンスの作成
const documentApi = new DocumentApi();
const processLockApi = new ProcessLockApi();

// カスタマイズ仕様書データの取得
export const fetchCustomizeSpecification = createAsyncThunk(
  "customizeSpecification/fetch",
  async (
    { docId, rowIndex }: { docId: string; rowIndex: number },
    { getState, rejectWithValue },
  ) => {
    try {
      const state = getState() as RootState;
      const { currentProject, currentUser } = state.auth;

      const response = await documentApi.get(currentUser.organization_id, currentProject.id, docId);

      if (!response?.content) {
        return rejectWithValue("ドキュメントの取得に失敗しました。");
      }

      const parsedContent = JSON.parse(response.content);
      const rows: string[][] = parsedContent.rows || [];

      if (rowIndex >= 0 && rowIndex < rows.length) {
        const rowArray = rows[rowIndex];
        if (rowArray) {
          return {
            機能名: rowArray[0] || "",
            適用アプリ名: rowArray[1] || "",
            概要: rowArray[2] || "",
            入力フィールド: rowArray[3] || "",
            出力フィールド: rowArray[4] || "",
            処理条件: rowArray[5] || "",
            機能詳細: rowArray[6] || "",
            設定情報: rowArray[7] || "",
            イベント契機: rowArray[8] || "",
            対応端末: rowArray[9] || "",
            対応区分: rowArray[10] || "",
          };
        } else {
          return rejectWithValue("行データが見つかりません");
        }
      } else {
        return rejectWithValue("指定された行が見つかりません");
      }
    } catch (_error) {
      return rejectWithValue("詳細データの取得中にエラーが発生しました。");
    }
  },
);

// ロックの取得
export const acquireLock = createAsyncThunk(
  "customizeSpecification/acquireLock",
  async (_, { getState, rejectWithValue }) => {
    try {
      const state = getState() as RootState;
      const { currentProject, currentUser } = state.auth;

      const response = await processLockApi.lock(currentUser.organization_id, currentProject.id, {
        type: "customize-specification",
        mode: "edit",
      });

      if (response === null) {
        return rejectWithValue("編集権限を取得できませんでした");
      }

      return true;
    } catch (_error) {
      return rejectWithValue("ロックの取得中にエラーが発生しました");
    }
  },
);

// ロックの解除
export const releaseLock = createAsyncThunk(
  "customizeSpecification/releaseLock",
  async (_, { getState, rejectWithValue }) => {
    try {
      const state = getState() as RootState;
      const { currentProject, currentUser } = state.auth;

      await processLockApi.unlock(currentUser.organization_id, currentProject.id, {
        type: "customize-specification",
        mode: "edit",
      });

      return true;
    } catch (_error) {
      return rejectWithValue("ロックの解除中にエラーが発生しました");
    }
  },
);

// データの更新
export const updateCustomizeSpecification = createAsyncThunk(
  "customizeSpecification/update",
  async (
    { docId, rowIndex }: { docId: string; rowIndex: number },
    { getState, dispatch, rejectWithValue },
  ) => {
    try {
      const state = getState() as RootState;
      const { currentProject, currentUser } = state.auth;
      const { data } = state.customizeSpecification;

      if (!data) {
        return rejectWithValue("保存に必要な情報が不足しています。");
      }

      const response = await documentApi.get(currentUser.organization_id, currentProject.id, docId);

      if (!response?.content) {
        return rejectWithValue("ドキュメントの取得に失敗しました");
      }

      const parsedContent = JSON.parse(response.content);
      const headers = parsedContent.headers || [];
      const rows: string[][] = parsedContent.rows || [];

      if (rowIndex >= 0 && rowIndex < rows.length) {
        const updatedRowArray = headers.map((header: keyof RowData) => data[header] || "");
        rows[rowIndex] = updatedRowArray;
      } else {
        return rejectWithValue("更新対象の行が見つかりません");
      }

      const updateDataPayload = { content: JSON.stringify({ headers, rows }) };
      const updateResponse = await documentApi.updateContent(
        currentUser.organization_id,
        currentProject.id,
        docId,
        updateDataPayload,
      );

      if (updateResponse === null) {
        return rejectWithValue("ドキュメントの更新に失敗しました");
      }

      // データの更新が成功したら、ロックを解除
      await dispatch(releaseLock());
      // ロック解除後にカスタマイズ仕様を再取得
      await dispatch(fetchCustomizeSpecification({ docId, rowIndex }));

      return true;
    } catch (error) {
      return rejectWithValue(`保存中にエラーが発生しました: ${(error as Error)?.message}`);
    }
  },
);

const customizeSpecificationSlice = createSlice({
  name: "customizeSpecification",
  initialState,
  reducers: {
    setData: (state, action: PayloadAction<RowData>) => {
      state.data = action.payload;
    },
    setCopyData: (state, action: PayloadAction<RowData>) => {
      state.copyData = action.payload;
    },
    restoreFromCopyData: (state) => {
      state.data = JSON.parse(JSON.stringify(state.copyData));
    },
    updateField: (state, action: PayloadAction<{ field: keyof RowData; value: string }>) => {
      if (state.data) {
        state.data[action.payload.field] = action.payload.value;
      }
    },
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // fetchCustomizeSpecification
      .addCase(fetchCustomizeSpecification.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(fetchCustomizeSpecification.fulfilled, (state, action) => {
        state.isLoading = false;
        state.data = action.payload;
        state.fetchError = null;
      })
      .addCase(fetchCustomizeSpecification.rejected, (state, action) => {
        state.isLoading = false;
        state.data = null;
        state.fetchError = action.payload as string;
      })
      // acquireLock
      .addCase(acquireLock.fulfilled, (state) => {
        state.isEditable = true;
        state.copyData = JSON.parse(JSON.stringify(state.data)); // 編集開始時にデータをコピー
      })
      .addCase(acquireLock.rejected, (state, action) => {
        state.error = action.payload as string;
      })
      // releaseLock
      .addCase(releaseLock.fulfilled, (state) => {
        state.isEditable = false;
      })
      .addCase(releaseLock.rejected, (state, action) => {
        state.error = action.payload as string;
      })
      // updateCustomizeSpecification
      .addCase(updateCustomizeSpecification.fulfilled, (state) => {
        state.isEditable = false;
      })
      .addCase(updateCustomizeSpecification.rejected, (state, action) => {
        state.error = action.payload as string;
      });
  },
});

export const { setData, setCopyData, restoreFromCopyData, updateField, clearError } =
  customizeSpecificationSlice.actions;

export default customizeSpecificationSlice.reducer;
